import React, { useEffect, useMemo } from 'react';
import {
  Card,
  Button,
  Form,
  Typography,
  Select,
  DatePicker,
  Row,
  Col,
  Switch,
  Divider,
  Input,
  Space,
  Spin,
  Alert,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, LinkOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { ProjectSelector } from '@/components/project/ProjectSelector';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useActiveCustomers } from '@/hooks/useCustomers';
import { useProjectsForSelection } from '@/hooks/useProject';
import { useCreateNCR, useNCR, useUpdateNCR } from '../../hooks/useQuality';
import { useWorkOrders } from '../../hooks/useWorkOrder';
import { useUsers } from '../../hooks/useUsers';
import { useQuery } from '@tanstack/react-query';
import { mocService } from '@/services/moc.service';
import { riskService } from '@/services/risk.service';
import employeeService, { employeeKeys } from '@/services/employee.service';

const { Title, Text } = Typography;
const { TextArea } = Input;

type NCRFormPageProps = {
  mode?: 'create' | 'edit';
};

function buildPayload(values: Record<string, any>) {
  const quantityAffected =
    values.quantity_affected === undefined ||
    values.quantity_affected === null ||
    values.quantity_affected === ''
      ? undefined
      : Number(values.quantity_affected);

  const toDate = (v: any) => (v ? v.format('YYYY-MM-DD') : null);

  return {
    source: values.source,
    severity: values.severity,
    category: values.category,
    description: values.description,
    location: values.location,
    affected_items: values.affected_items,
    quantity_affected: quantityAffected,
    quantity_uom: values.quantity_uom || null,
    project_id: values.project_id || null,
    work_order_id: values.work_order_id || null,
    department_id: values.department_id || null,
    inspection_record_id: values.inspection_record_id || null,
    source_reference: values.source_reference,
    identified_date: values.identified_date
      ? values.identified_date.format('YYYY-MM-DD')
      : new Date().toISOString().split('T')[0],
    identified_time: values.identified_time
      ? values.identified_time.format('HH:mm:ss')
      : undefined,
    capa_required: values.capa_required || false,
    is_potential_nc: values.is_potential_nc || false,
    containment_action: values.containment_action,
    correction_owner_user_id: values.correction_owner_user_id || null,
    capa_owner_user_id: values.capa_owner_user_id || null,
    moc_required: values.moc_required || false,
    moc_id: values.moc_id || null,
    moc_ref_no: values.moc_ref_no || null,
    risk_assessment_id: values.risk_assessment_id || null,
    risk_reference: values.risk_reference || null,
    opportunity_for_improvement: values.opportunity_for_improvement || false,
    correction_target_date: toDate(values.correction_target_date),
    correction_action: values.correction_action,
    correction_completed_by: values.correction_completed_by || null,
    correction_completed_date: toDate(values.correction_completed_date),
    correction_delay_justification: values.correction_delay_justification,
    capa_target_date: toDate(values.capa_target_date),
    capa_delay_justification: values.capa_delay_justification,
    verification_method: values.verification_method,
    verified_by_designation: values.verified_by_designation,
    verification_evidence_notes: values.verification_evidence_notes,
    supplier_name: values.supplier_name,
    supplier_contact_person: values.supplier_contact_person,
    package_no: values.package_no,
    po_number: values.po_number,
    po_line: values.po_line,
    part_item_no: values.part_item_no,
    part_description: values.part_description,
    equipment_description: values.equipment_description,
    tag_number: values.tag_number,
    drawing_no: values.drawing_no,
    drawing_revision: values.drawing_revision,
    specification_number: values.specification_number,
    reference_documents: values.reference_documents,
    international_standard: values.international_standard,
    actual_requirements: values.actual_requirements,
    additional_information: values.additional_information,
    proposed_corrective_action: values.proposed_corrective_action,
    customer_comments: values.customer_comments,
    customer_decision: values.customer_decision,
    customer_decision_date: toDate(values.customer_decision_date),
    supplier_signer_name: values.supplier_signer_name,
    supplier_signer_title: values.supplier_signer_title,
    customer_signer_name: values.customer_signer_name,
    customer_signer_title: values.customer_signer_title,
  };
}

const NCRFormPage: React.FC<NCRFormPageProps> = ({ mode = 'create' }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = mode === 'edit';
  const [form] = Form.useForm();

  const watchedCustomerId = Form.useWatch('customer_id', form);
  const watchedProjectId = Form.useWatch('project_id', form);

  const { data: ncr, isLoading: ncrLoading } = useNCR(isEdit ? id || '' : '');
  const { data: customers = [], isLoading: customersLoading } = useActiveCustomers();
  const { data: projects = [] } = useProjectsForSelection();
  const { data: workOrders = [], isLoading: workOrdersLoading } = useWorkOrders();
  const { data: users = [] } = useUsers({ is_active: true });
  const { data: employees = [] } = useQuery({
    queryKey: employeeKeys.list(),
    queryFn: () => employeeService.list(),
    staleTime: 60_000,
  });
  const { data: mocList = [] } = useQuery({
    queryKey: ['moc-list-for-ncr'],
    queryFn: () => mocService.list(),
    staleTime: 60_000,
  });
  const { data: riskAssessments = [] } = useQuery({
    queryKey: ['risk-assessments-for-ncr'],
    queryFn: () => riskService.getRiskAssessments(),
    staleTime: 60_000,
  });
  const createMutation = useCreateNCR();
  const updateMutation = useUpdateNCR();

  const userOptions = useMemo(
    () =>
      (users as any[]).map((u) => ({
        value: u.id,
        label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || u.id,
      })),
    [users]
  );

  /** CAPA responsible_person_id is usually an employee id — map to user id for NCR CA owner. */
  const capaOwnerFromLinkedCapa = useMemo(() => {
    const capa = ncr?.capa;
    if (!capa?.responsible_person_id) return undefined;
    const rid = capa.responsible_person_id;
    if ((users as any[]).some((u) => u.id === rid)) return rid;
    const emp = (employees as any[]).find((e) => e.id === rid);
    return emp?.user_id ? (emp.user_id as string) : undefined;
  }, [ncr?.capa, users, employees]);

  const linkedCapaResponsibleLabel = useMemo(() => {
    const capa = ncr?.capa;
    if (!capa?.responsible_person_id) return null;
    if (capa.responsible_person_name) return capa.responsible_person_name;
    const emp = (employees as any[]).find((e) => e.id === capa.responsible_person_id);
    if (emp) {
      return (
        `${emp.employee_number || ''} ${emp.first_name || ''} ${emp.last_name || ''}`.trim() ||
        emp.id
      );
    }
    const u = (users as any[]).find((x) => x.id === capa.responsible_person_id);
    if (u) {
      return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || u.id;
    }
    return capa.responsible_person_id;
  }, [ncr?.capa, employees, users]);

  const selectedCustomerName = useMemo(() => {
    const match = (customers as any[]).find((c) => c.id === watchedCustomerId);
    return (match?.customer_name as string | undefined) || undefined;
  }, [customers, watchedCustomerId]);

  const filteredWorkOrders = useMemo(() => {
    const rows = workOrders as any[];
    if (watchedProjectId) {
      return rows.filter((wo) => wo.project_id === watchedProjectId);
    }
    if (watchedCustomerId && selectedCustomerName) {
      const name = selectedCustomerName.trim().toLowerCase();
      const projectIdsForCustomer = new Set(
        (projects || [])
          .filter(
            (p) =>
              p.customer_id === watchedCustomerId ||
              (p.customer_name || '').trim().toLowerCase() === name
          )
          .map((p) => p.id)
      );
      return rows.filter((wo) => {
        if (wo.project_id && projectIdsForCustomer.has(wo.project_id)) return true;
        const woCustomer = String(wo.customer_name || '').trim().toLowerCase();
        return Boolean(woCustomer && woCustomer === name);
      });
    }
    return rows;
  }, [workOrders, watchedProjectId, watchedCustomerId, selectedCustomerName, projects]);

  useEffect(() => {
    if (!isEdit || !ncr) return;
    const capa = ncr.capa;
    const first = <T,>(...vals: Array<T | null | undefined>) =>
      vals.find((v) => v != null && String(v).trim() !== '') as T | undefined;
    const capaTarget = first(ncr.capa_target_date, capa?.target_completion_date);

    form.setFieldsValue({
      source: ncr.source,
      severity: ncr.severity,
      category: ncr.category,
      description: ncr.description,
      location: ncr.location,
      affected_items: ncr.affected_items,
      quantity_affected: ncr.quantity_affected,
      quantity_uom: ncr.quantity_uom,
      project_id: ncr.project_id,
      work_order_id: ncr.work_order_id || undefined,
      department_id: ncr.department_id || undefined,
      inspection_record_id: ncr.inspection_record_id || undefined,
      source_reference: ncr.source_reference,
      identified_date: ncr.identified_date ? dayjs(ncr.identified_date) : dayjs(),
      capa_required: ncr.capa_required || Boolean(capa?.id),
      is_potential_nc: ncr.is_potential_nc,
      containment_action: ncr.containment_action,
      correction_owner_user_id: ncr.correction_owner_user_id || undefined,
      capa_owner_user_id: ncr.capa_owner_user_id || capaOwnerFromLinkedCapa || undefined,
      moc_required: ncr.moc_required,
      moc_id: ncr.moc_id || undefined,
      moc_ref_no: ncr.moc_ref_no,
      risk_assessment_id: ncr.risk_assessment_id || undefined,
      risk_reference: ncr.risk_reference,
      opportunity_for_improvement: ncr.opportunity_for_improvement,
      correction_target_date: ncr.correction_target_date
        ? dayjs(ncr.correction_target_date)
        : undefined,
      correction_action: ncr.correction_action,
      correction_completed_by: ncr.correction_completed_by || undefined,
      correction_completed_date: ncr.correction_completed_date
        ? dayjs(ncr.correction_completed_date)
        : undefined,
      correction_delay_justification: ncr.correction_delay_justification,
      capa_target_date: capaTarget ? dayjs(capaTarget) : undefined,
      capa_delay_justification: first(ncr.capa_delay_justification, capa?.extension_reason),
      verification_method: ncr.verification_method,
      verified_by_designation: ncr.verified_by_designation,
      verification_evidence_notes: ncr.verification_evidence_notes,
      supplier_name: ncr.supplier_name,
      supplier_contact_person: ncr.supplier_contact_person,
      package_no: ncr.package_no,
      po_number: ncr.po_number,
      po_line: ncr.po_line,
      part_item_no: ncr.part_item_no,
      part_description: ncr.part_description,
      equipment_description: ncr.equipment_description,
      tag_number: ncr.tag_number,
      drawing_no: ncr.drawing_no,
      drawing_revision: ncr.drawing_revision,
      specification_number: ncr.specification_number,
      reference_documents: ncr.reference_documents,
      international_standard: ncr.international_standard,
      actual_requirements: ncr.actual_requirements,
      additional_information: ncr.additional_information,
      proposed_corrective_action: first(
        ncr.proposed_corrective_action,
        capa?.action_plan,
        capa?.description
      ),
      customer_comments: ncr.customer_comments,
      customer_decision: ncr.customer_decision,
      customer_decision_date: ncr.customer_decision_date
        ? dayjs(ncr.customer_decision_date)
        : undefined,
      supplier_signer_name: ncr.supplier_signer_name,
      supplier_signer_title: ncr.supplier_signer_title,
      customer_signer_name: ncr.customer_signer_name,
      customer_signer_title: ncr.customer_signer_title,
    });
  }, [form, isEdit, ncr, capaOwnerFromLinkedCapa]);

  useEffect(() => {
    if (!isEdit || !ncr?.project_id || !projects?.length) return;
    if (form.getFieldValue('customer_id')) return;
    const project = projects.find((p) => p.id === ncr.project_id);
    if (project?.customer_id) {
      form.setFieldValue('customer_id', project.customer_id);
    }
  }, [form, isEdit, ncr?.project_id, projects]);

  const handleCustomerChange = (customerId: string | undefined) => {
    form.setFieldValue('customer_id', customerId);
    const customerName = String(
      (customers as any[]).find((c) => c.id === customerId)?.customer_name || ''
    )
      .trim()
      .toLowerCase();

    const projectId = form.getFieldValue('project_id');
    if (projectId && customerId) {
      const project = projects.find((p) => p.id === projectId);
      const stillValid =
        project?.customer_id === customerId ||
        (!!customerName &&
          (project?.customer_name || '').trim().toLowerCase() === customerName);
      if (!stillValid) {
        form.setFieldValue('project_id', undefined);
        form.setFieldValue('work_order_id', undefined);
        return;
      }
    }

    const woId = form.getFieldValue('work_order_id');
    if (woId && customerId) {
      const wo = (workOrders as any[]).find((w) => w.id === woId);
      const projectIdsForCustomer = new Set(
        (projects || [])
          .filter(
            (p) =>
              p.customer_id === customerId ||
              (!!customerName && (p.customer_name || '').trim().toLowerCase() === customerName)
          )
          .map((p) => p.id)
      );
      const woOk =
        (wo?.project_id && projectIdsForCustomer.has(wo.project_id)) ||
        (!!customerName && String(wo?.customer_name || '').trim().toLowerCase() === customerName);
      if (!woOk) {
        form.setFieldValue('work_order_id', undefined);
      }
    }
  };

  const handleSubmit = async (values: Record<string, any>) => {
    const payload = buildPayload(values);
    if (isEdit && id) {
      await updateMutation.mutateAsync({ id, data: payload });
      navigate(`/qms/ncrs/${id}`);
      return;
    }
    const created = await createMutation.mutateAsync(payload);
    navigate(`/qms/ncrs/${created.id}`);
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  if (isEdit && ncrLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(isEdit && id ? `/qms/ncrs/${id}` : '/qms/ncrs')}
        >
          Back
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          {isEdit ? `Edit NCR${ncr?.ncr_number ? ` — ${ncr.ncr_number}` : ''}` : 'Create Non-Conformance Report'}
        </Title>
      </Space>

      {isEdit && ncr?.capa && (
        <Alert
          type="info"
          showIcon
          icon={<LinkOutlined />}
          style={{ marginBottom: 16 }}
          message={`Linked CAPA ${ncr.capa.capa_number || ncr.capa_id}`}
          description={
            <Space direction="vertical" size={0}>
              <Text type="secondary">
                Corrective Action fields below are filled from this CAPA when the NCR snapshot is empty.
              </Text>
              {linkedCapaResponsibleLabel && (
                <Text type="secondary">Responsible: {linkedCapaResponsibleLabel}</Text>
              )}
              {ncr.capa.target_completion_date && (
                <Text type="secondary">
                  Target: {dayjs(ncr.capa.target_completion_date).format('DD/MM/YYYY')}
                </Text>
              )}
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() => navigate(`/qms/capas/${ncr.capa_id || ncr.capa?.id}`)}
              >
                Open CAPA
              </Button>
            </Space>
          }
        />
      )}

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ identified_date: dayjs(), capa_required: false }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="source"
                label="Source"
                rules={[{ required: true, message: 'Please select source' }]}
              >
                <Select placeholder="Select source">
                  <Select.Option value="Inspection">Inspection</Select.Option>
                  <Select.Option value="Internal Audit">Internal Audit</Select.Option>
                  <Select.Option value="Customer Complaint">Customer Complaint</Select.Option>
                  <Select.Option value="Supplier Issue">Supplier Issue</Select.Option>
                  <Select.Option value="Self-Identified">Self-Identified</Select.Option>
                  <Select.Option value="Measuring Equipment">Measuring Equipment</Select.Option>
                  <Select.Option value="Other">Other</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="severity"
                label="Severity"
                rules={[{ required: true, message: 'Please select severity' }]}
              >
                <Select placeholder="Select severity">
                  <Select.Option value="Critical">Critical</Select.Option>
                  <Select.Option value="Major">Major</Select.Option>
                  <Select.Option value="Minor">Minor</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="category" label="Category">
                <Select placeholder="Select category" allowClear>
                  <Select.Option value="Material">Material</Select.Option>
                  <Select.Option value="Process">Process</Select.Option>
                  <Select.Option value="Documentation">Documentation</Select.Option>
                  <Select.Option value="Design">Design</Select.Option>
                  <Select.Option value="Workmanship">Workmanship</Select.Option>
                  <Select.Option value="Other">Other</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="customer_id" label="Customer">
                <Select
                  allowClear
                  showSearch
                  loading={customersLoading}
                  placeholder="Filter by customer"
                  optionFilterProp="label"
                  options={(customers as any[]).map((c) => ({
                    value: c.id,
                    label: c.customer_name || c.id,
                  }))}
                  onChange={(v) => handleCustomerChange(v)}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="project_id" label="Project">
                <ProjectSelector
                  allowClear
                  placeholder={watchedCustomerId ? 'Select project' : 'Select customer to filter projects'}
                  customerId={watchedCustomerId}
                  customerName={selectedCustomerName}
                  onChange={(projectId, projectData) => {
                    form.setFieldValue('project_id', projectId);
                    if (projectData?.customer_id && !form.getFieldValue('customer_id')) {
                      form.setFieldValue('customer_id', projectData.customer_id);
                    }
                    const woId = form.getFieldValue('work_order_id');
                    if (woId && projectId) {
                      const wo = (workOrders as any[]).find((w) => w.id === woId);
                      if (wo?.project_id && wo.project_id !== projectId) {
                        form.setFieldValue('work_order_id', undefined);
                      }
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="work_order_id" label="Work Order">
                <Select
                  allowClear
                  showSearch
                  loading={workOrdersLoading}
                  placeholder={
                    watchedProjectId
                      ? 'Select work order for project'
                      : watchedCustomerId
                        ? 'Select work order for customer'
                        : 'Select customer/project to filter'
                  }
                  optionFilterProp="label"
                  options={filteredWorkOrders.map((wo) => ({
                    value: wo.id,
                    label: wo.work_order_number || wo.id,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="department_id" label="Department">
                <DepartmentSelect allowClear placeholder="Dept of the NC" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="identified_date" label="Identified Date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="source_reference" label="Customer / External NCR No.">
                <Input placeholder="e.g. 71220, MRR-189" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="is_potential_nc" label="Potential NC" valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="capa_required" label="CAPA Required" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="opportunity_for_improvement"
                label="Opportunity for improvement"
                valuePropName="checked"
              >
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            Ownership / MOC / Risk
          </Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="correction_owner_user_id" label="Responsible for Correction">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Immediate fix owner"
                  options={userOptions}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="capa_owner_user_id" label="Responsible for Corrective Action">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Systemic CA owner"
                  options={userOptions}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="moc_required" label="MOC Required" valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item name="moc_id" label="MOC link">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select MOC"
                  options={(mocList as any[]).map((m) => ({
                    value: m.id,
                    label: `${m.moc_number} — ${m.change_title || ''}`.trim(),
                  }))}
                  onChange={(mocId) => {
                    const match = (mocList as any[]).find((m) => m.id === mocId);
                    if (match?.moc_number) {
                      form.setFieldValue('moc_ref_no', match.moc_number);
                      form.setFieldValue('moc_required', true);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item name="moc_ref_no" label="MOC Ref No">
                <Input placeholder="Manual ref if no link" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="risk_assessment_id" label="Risk assessment">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select risk assessment"
                  options={(riskAssessments as any[]).map((r) => ({
                    value: r.id,
                    label: r.assessment_number || r.id,
                  }))}
                  onChange={(riskId) => {
                    const match = (riskAssessments as any[]).find((r) => r.id === riskId);
                    if (match?.assessment_number) {
                      form.setFieldValue('risk_reference', match.assessment_number);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="risk_reference" label="Risk Reference">
                <Input placeholder="Risk ref / number" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            Supplier / PO
          </Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="supplier_name" label="Supplier name">
                <Input placeholder="Supplier / vendor name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplier_contact_person" label="Supplier contact person">
                <Input placeholder="Contact name" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="package_no" label="Package No">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="po_number" label="PO No">
                <Input placeholder="e.g. 27064" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="po_line" label="PO Line">
                <Input placeholder="e.g. 0001" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            Part / Equipment
          </Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="part_item_no" label="Part / Item No.">
                <Input placeholder="e.g. 132138+30" />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="part_description" label="Description">
                <Input placeholder="e.g. GUIDE, JAW PIPE PRS8" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="equipment_description" label="Equipment description">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="tag_number" label="Tag number">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            References
          </Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="drawing_no" label="Drawing Nr">
                <Input placeholder="e.g. 132136-ASM" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="drawing_revision" label="Drawing Revision">
                <Input placeholder="e.g. -" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="specification_number" label="Specification number">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="reference_documents" label="Reference documents">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="international_standard" label="International standard / others">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            Non-conformance
          </Divider>
          <Form.Item name="actual_requirements" label="Actual Requirements">
            <TextArea rows={2} placeholder="e.g. Welding without any defects" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description of Non-Conformance"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea rows={4} placeholder="Describe the non-conformance in detail" />
          </Form.Item>
          <Form.Item name="additional_information" label="Additional Information">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="containment_action" label="Containment / immediate action">
            <TextArea
              rows={2}
              placeholder="Stop ship / quarantine / segregate / hold — before disposition"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="location" label="Location">
                <Input placeholder="Where was the non-conformance identified?" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="affected_items" label="Affected Items">
                <Input placeholder="Items/products affected" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="quantity_affected" label="Quantity Affected">
                <Input type="number" placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="quantity_uom" label="UOM">
                <Input placeholder="e.g. EA, m, kg" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="inspection_record_id" label="Inspection record ID">
                <Input placeholder="Optional linked inspection UUID" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            Correction (this instance)
          </Divider>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Correction vs Corrective Action"
            description={
              <Text type="secondary">
                Correction fixes this non-conforming instance. Corrective Action / CAPA addresses the
                systemic root cause so it does not recur.
              </Text>
            }
          />
          <Form.Item name="correction_action" label="Correction description (action taken)">
            <TextArea rows={2} placeholder="What was done to fix this instance?" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="correction_target_date" label="Correction target date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="correction_completed_by" label="Correction completed by">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={userOptions}
                  placeholder="Select user"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="correction_completed_date" label="Correction completion date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="correction_delay_justification" label="Late justification (if past target)">
            <TextArea rows={2} />
          </Form.Item>

          <Divider orientation="left" plain>
            Corrective Action / Improvement
          </Divider>
          <Form.Item name="proposed_corrective_action" label="Proposed / systemic corrective action">
            <TextArea rows={2} placeholder="Ties to root cause / controls; detail lives on CAPA when linked" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="capa_target_date" label="CA target date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="capa_delay_justification" label="CA late justification">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            Verification (preview)
          </Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="verification_method" label="Verification method">
                <Input placeholder="e.g. Re-inspection, document review" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="verified_by_designation" label="Verified by designation">
                <Input placeholder="Role / title" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="verification_evidence_notes" label="Verification evidence notes">
            <TextArea rows={2} placeholder="Photos, activities, or reference to Documents tab" />
          </Form.Item>

          <Divider orientation="left" plain>
            Customer decision / signatures
          </Divider>
          <Form.Item name="customer_comments" label="Customer comments / recommendations">
            <TextArea rows={2} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="customer_decision" label="Customer Accepted / Rejected">
                <Select allowClear placeholder="Select">
                  <Select.Option value="Accepted">Accepted</Select.Option>
                  <Select.Option value="Rejected">Rejected</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="customer_decision_date" label="Decision date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="supplier_signer_name" label="Supplier signer name">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplier_signer_title" label="Supplier signer title">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="customer_signer_name" label="Customer signer name">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="customer_signer_title" label="Customer signer title">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Space>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
              {isEdit ? 'Save changes' : 'Create NCR'}
            </Button>
            <Button onClick={() => navigate(isEdit && id ? `/qms/ncrs/${id}` : '/qms/ncrs')}>
              Cancel
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default NCRFormPage;
